import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase_client';
import {
  RELOGIN_MODAL_OPEN_EVENT,
  RELOGIN_MODAL_CLOSE_EVENT,
  getReloginModalContext,
  setReloginGuard,
} from '../utils/reloginModalBus';

/**
 * App-wide session-expiry recovery modal — mounted ONCE at the root (index.tsx),
 * outside every page's component tree and <form>, so its inputs/clicks can never
 * bubble into page-level handlers (e.g. EditFilings validation listeners).
 * Its inputs are additionally marked data-skip-validation for the document-level
 * blur/change validation listeners, which fire regardless of DOM position.
 *
 * Opened only on Save-time recovery: EditFilings detects a truly dead session
 * before/during save and opens this modal with an onSuccess that retries save.
 * AuthContext deliberately does NOT open this on ambient SIGNED_OUT events
 * (those are recovered silently to preserve "once login, forever login").
 *
 * The user re-enters their password WITHOUT navigating away, so the page DOM
 * (and all unsaved work) stays intact. Visibility is driven by document events
 * (see reloginModalBus); internal state only re-renders this modal.
 */
export const ReloginModal: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  const [cancelLabel, setCancelLabel] = useState('Cancel');

  const open = useCallback(() => {
    const ctx = getReloginModalContext();
    setEmail(ctx?.email ?? '');
    setPassword('');
    setError(null);
    setSigningIn(false);
    setCancelLabel(ctx?.cancelLabel ?? 'Cancel');
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    document.addEventListener(RELOGIN_MODAL_OPEN_EVENT, open);
    document.addEventListener(RELOGIN_MODAL_CLOSE_EVENT, close);
    return () => {
      document.removeEventListener(RELOGIN_MODAL_OPEN_EVENT, open);
      document.removeEventListener(RELOGIN_MODAL_CLOSE_EVENT, close);
    };
  }, [open, close]);

  // NOTE: not a <form> submit — this modal lives INSIDE EditFilings' <form>,
  // and a nested form would bubble submit/validation into the filing form.
  const handleSignIn = async () => {
    if (!email || !password || signingIn) return;
    try {
      setSigningIn(true);
      setError(null);
      // Background token continuation only: the relogin guard (set by the caller
      // before this modal opened) keeps AuthContext from reacting to the auth
      // events this call fires, so nothing re-renders or navigates.
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) {
        // Wrong password etc — stay in the dialog with an inline error
        setError(signInError.message || 'Sign in failed. Please check your password.');
        return;
      }
      const onSuccess = getReloginModalContext()?.onSuccess;
      setVisible(false);
      // Let the modal close before retrying (avoids overlapping UI work).
      // The guard is released by the retried save once the session checks out.
      if (onSuccess) queueMicrotask(onSuccess);
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleCancel = () => {
    // User opted out of recovery — release the guard so normal auth behavior resumes.
    // EditFilings Save flow has no onCancel (just dismiss; stay on the page).
    setReloginGuard(false);
    setVisible(false);
    const onCancel = getReloginModalContext()?.onCancel;
    if (onCancel) queueMicrotask(onCancel);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10001 }}>
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="text-center mb-5">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 bg-amber-100">
            <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Session Expired</h3>
          <p className="text-sm text-gray-600">
            Your login session has expired. Sign in again to continue —{' '}
            <strong>your unsaved work is preserved</strong> as long as you stay on this page.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              readOnly
              data-skip-validation="true"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Keep Enter inside the modal — never let it reach the filing form
                  e.preventDefault();
                  e.stopPropagation();
                  handleSignIn();
                }
              }}
              placeholder="Enter your password"
              autoFocus
              data-skip-validation="true"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={signingIn}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm font-medium"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleSignIn}
              disabled={!password || signingIn}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
            >
              {signingIn && (
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {signingIn ? 'Signing in…' : 'Sign In & Continue'}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center">
            Do not close or refresh this page, or your unsaved changes will be lost.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReloginModal;
